import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { analyticsAPI, vehiclesAPI } from "@/lib/api";
import { HeaderIcon } from "@/lib/assets";
import { toast } from "sonner";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Legend,
} from "recharts";
import {
  ArrowLeft,
  Loader2,
  DollarSign,
  TrendingUp,
  Wrench,
  Package,
  Truck,
  PieChart as PieChartIcon,
  BarChart3,
} from "lucide-react";

const COLORS = ["#f97316", "#3b82f6", "#22c55e", "#eab308", "#8b5cf6", "#ec4899", "#06b6d4", "#f43f5e"];

const formatCurrency = (value) => `$${value.toLocaleString()}`;

export default function Analytics() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [vehicles, setVehicles] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(searchParams.get("vehicle") || "all");
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    loadVehicles();
  }, []);

  useEffect(() => {
    loadAnalytics();
  }, [selectedVehicle]);

  const loadVehicles = async () => {
    try {
      const { data } = await vehiclesAPI.list();
      setVehicles(data);
    } catch (error) {
      toast.error("Failed to load vehicles");
    }
  };

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      if (selectedVehicle === "all") {
        const { data } = await analyticsAPI.overview();
        setAnalytics(data);
      } else {
        const { data } = await analyticsAPI.vehicle(selectedVehicle);
        setAnalytics(data);
      }
    } catch (error) {
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  const laborData = analytics ? [
    { name: "DIY", value: selectedVehicle === "all" ? analytics.labor_breakdown?.diy?.cost : analytics.labor_breakdown?.diy, fill: "#22c55e" },
    { name: "Shop", value: selectedVehicle === "all" ? analytics.labor_breakdown?.shop?.cost : analytics.labor_breakdown?.shop, fill: "#3b82f6" },
  ].filter(d => d.value > 0) : [];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card/50 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link
                to="/"
                data-testid="back-btn"
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div className="flex items-center gap-3">
                <HeaderIcon />

                <h1 className="text-lg font-bold text-foreground">
                  Cost Analytics
                </h1>
              </div>
            </div>
            
            {/* Vehicle Selector */}
            <Select value={selectedVehicle} onValueChange={setSelectedVehicle}>
              <SelectTrigger data-testid="vehicle-selector" className="w-48 bg-zinc-900 border-zinc-800">
                <SelectValue placeholder="Select vehicle" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-800">
                <SelectItem value="all">All Vehicles</SelectItem>
                {vehicles.map((v) => (
                  <SelectItem key={v.id} value={v.id}>
                    {v.nickname}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : !analytics ? (
          <Card className="card-tactical bg-zinc-950/50 border-zinc-800">
            <CardContent className="p-8 text-center">
              <p className="text-muted-foreground">No data available</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Summary Stats */}
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Card className="card-tactical bg-zinc-950/50 border-zinc-800">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-500/10 rounded-sm flex items-center justify-center">
                      <DollarSign className="w-5 h-5 text-green-500" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground uppercase">Total Invested</p>
                      <p className="text-xl font-mono font-bold text-foreground">
                        ${analytics.totals?.total_invested?.toLocaleString() || 0}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="card-tactical bg-zinc-950/50 border-zinc-800">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-500/10 rounded-sm flex items-center justify-center">
                      <Wrench className="w-5 h-5 text-blue-500" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground uppercase">Service Cost</p>
                      <p className="text-xl font-mono font-bold text-foreground">
                        ${analytics.totals?.service_cost?.toLocaleString() || 0}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="card-tactical bg-zinc-950/50 border-zinc-800">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-500/10 rounded-sm flex items-center justify-center">
                      <Package className="w-5 h-5 text-purple-500" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground uppercase">Mod Cost</p>
                      <p className="text-xl font-mono font-bold text-foreground">
                        ${analytics.totals?.mod_cost?.toLocaleString() || 0}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {selectedVehicle !== "all" && analytics.totals?.cost_per_mile !== undefined ? (
                <Card className="card-tactical bg-zinc-950/50 border-zinc-800">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-amber-500/10 rounded-sm flex items-center justify-center">
                        <TrendingUp className="w-5 h-5 text-amber-500" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground uppercase">Cost/Mile</p>
                        <p className="text-xl font-mono font-bold text-foreground">
                          ${analytics.totals.cost_per_mile}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card className="card-tactical bg-zinc-950/50 border-zinc-800">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-amber-500/10 rounded-sm flex items-center justify-center">
                        <Truck className="w-5 h-5 text-amber-500" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground uppercase">Vehicles</p>
                        <p className="text-xl font-mono font-bold text-foreground">
                          {analytics.totals?.vehicle_count || 0}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Charts Row */}
            <div className="grid gap-6 lg:grid-cols-2">
              {/* Monthly Spending */}
              <Card className="card-tactical bg-zinc-950/50 border-zinc-800">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-muted-foreground" />
                    Monthly Spending
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {analytics.monthly_spending?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <LineChart data={analytics.monthly_spending}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                        <XAxis 
                          dataKey="month" 
                          stroke="#666"
                          tick={{ fill: "#999", fontSize: 11 }}
                          tickFormatter={(value) => {
                            const [year, month] = value.split("-");
                            return `${month}/${year.slice(2)}`;
                          }}
                        />
                        <YAxis 
                          stroke="#666" 
                          tick={{ fill: "#999", fontSize: 11 }}
                          tickFormatter={(value) => `$${value}`}
                        />
                        <Tooltip 
                          contentStyle={{ backgroundColor: "#18181b", border: "1px solid #333" }}
                          formatter={(value) => [formatCurrency(value), "Cost"]}
                        />
                        <Line 
                          type="monotone" 
                          dataKey="cost" 
                          stroke="#f97316" 
                          strokeWidth={2}
                          dot={{ fill: "#f97316", r: 4 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                      No spending data yet
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* DIY vs Shop */}
              <Card className="card-tactical bg-zinc-950/50 border-zinc-800">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <PieChartIcon className="w-5 h-5 text-muted-foreground" />
                    DIY vs Shop Labor
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {laborData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie
                          data={laborData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={90}
                          paddingAngle={5}
                          dataKey="value"
                          label={({ name, value }) => `${name}: $${value.toLocaleString()}`}
                        >
                          {laborData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.fill} />
                          ))}
                        </Pie>
                        <Tooltip 
                          contentStyle={{ backgroundColor: "#18181b", border: "1px solid #333" }}
                          formatter={(value) => formatCurrency(value)}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                      No labor data yet
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Category Breakdowns */}
            <div className="grid gap-6 lg:grid-cols-2">
              {/* Service Cost by Category */}
              <Card className="card-tactical bg-zinc-950/50 border-zinc-800">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Wrench className="w-5 h-5 text-muted-foreground" />
                    Service Cost by Category
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {analytics.service_cost_by_category?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart 
                        data={analytics.service_cost_by_category.slice(0, 8)} 
                        layout="vertical"
                        margin={{ left: 20 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#333" horizontal={false} />
                        <XAxis 
                          type="number" 
                          stroke="#666"
                          tick={{ fill: "#999", fontSize: 11 }}
                          tickFormatter={(value) => `$${value}`}
                        />
                        <YAxis 
                          type="category" 
                          dataKey="category" 
                          stroke="#666"
                          tick={{ fill: "#999", fontSize: 11 }}
                          width={100}
                        />
                        <Tooltip 
                          contentStyle={{ backgroundColor: "#18181b", border: "1px solid #333" }}
                          formatter={(value) => formatCurrency(value)}
                        />
                        <Bar dataKey="cost" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                      No service cost data
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Mod Cost by Category */}
              <Card className="card-tactical bg-zinc-950/50 border-zinc-800">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Package className="w-5 h-5 text-muted-foreground" />
                    Mod Cost by Category
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {analytics.mod_cost_by_category?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart 
                        data={analytics.mod_cost_by_category.slice(0, 8)} 
                        layout="vertical"
                        margin={{ left: 20 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#333" horizontal={false} />
                        <XAxis 
                          type="number" 
                          stroke="#666"
                          tick={{ fill: "#999", fontSize: 11 }}
                          tickFormatter={(value) => `$${value}`}
                        />
                        <YAxis 
                          type="category" 
                          dataKey="category" 
                          stroke="#666"
                          tick={{ fill: "#999", fontSize: 11 }}
                          width={120}
                        />
                        <Tooltip 
                          contentStyle={{ backgroundColor: "#18181b", border: "1px solid #333" }}
                          formatter={(value) => formatCurrency(value)}
                        />
                        <Bar dataKey="cost" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                      No mod cost data
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
